import _ from 'lodash';
import { Dispatch } from 'redux';

import WebglSpecs from 'uf/ui/base/webgl';
import { getUser } from 'uf/user/selectors/user';

import * as amplitudeActionTypes from './ActionTypes';
import { getAmplitude, getAmplitudeApiEndpoint, getAmplitudeId } from './utils';
import { OrganizationInfo } from 'uf-api-rtk/store/Api';

const amp = getAmplitude();
const ampId = getAmplitudeId();
const apiEndpoint = getAmplitudeApiEndpoint();

export function updateAmplitudeUserProperty(
  key: string,
  value: string | boolean | OrganizationInfo[],
) {
  return (dispatch: Dispatch, getState) => {
    if (!(__CLIENT__ || __TESTING__)) {
      return;
    }

    const user = getUser(getState());

    if (_.isEmpty(user) || user.disable_user_log) {
      return;
    }
    if (amp) {
      const identifyEvent = new amp.Identify();
      identifyEvent.set(key, value);
      if (key === 'active_org_key') {
        const activeOrgName = user.organizations?.find(
          org => org.key === user.active_org_key,
        )?.name;
        identifyEvent.set('active_org_name', activeOrgName);
      }
      amp.identify(identifyEvent);
    }
    dispatch({
      type: amplitudeActionTypes.UPDATE,
    });
  };
}

export function initializeAmplitude(amplitude = amp, amplitudeId = ampId) {
  return (dispatch: Dispatch, getState) => {
    if (!(__CLIENT__ || __TESTING__)) {
      return;
    }

    const user = getUser(getState());

    // Don't initialize Amplitude if the user is configured
    // to disallow event logging.
    if (user.disable_user_log) {
      return;
    }

    const webgl = WebglSpecs();
    const settings: any = {};
    if (apiEndpoint) {
      settings.serverUrl = apiEndpoint;
    }
    if (amplitude) {
      if (_.isEmpty(user)) {
        amplitude.init(amplitudeId, null, settings);
      } else {
        const userProperties = {
          key: user.key,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          num_organizations: user.organizations.length,
          // amplitude ignores values in lists that aren't strings or numbers
          organizations: user.organizations.map(
            org => `(${org.name}, ${org.key})`,
          ),
          active_org_key: user.active_org_key,
          active_org_name: user.organizations?.find(
            org => org.key === user.active_org_key,
          )?.name,
          username: user.username,
          uf_frontend_version: __BUILD_INFO__.git_commit,
          webgl: JSON.stringify(webgl),
        };
        // This isn't always present, but if it is we want to call it out
        // separately for amplitude data collection
        if (webgl && 'majorPerformanceCaveat' in webgl) {
          userProperties['webgl.majorPerformanceCaveat'] =
            webgl.majorPerformanceCaveat;
        }

        amplitude.init(amplitudeId, user.email, settings);
        const identifyEvent = new amplitude.Identify();
        Object.keys(userProperties).forEach(key => {
          identifyEvent.set(key, userProperties[key]);
        });
        amplitude.identify(identifyEvent);
      }
    }

    dispatch({
      type: amplitudeActionTypes.INITIALIZE,
    });
  };
}
